import { usePageContext } from "vike-react/usePageContext";
import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import { ROUTES } from "@/data/navigation";
import { colorAlpha } from "@/lib/utils";
import styles from "./Nav.module.css";

export default function Nav() {
  const { urlPathname: pathname } = usePageContext();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  /* document.body does not exist while Vike prerenders. */
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  /* Close drawer on route change */
  useEffect(() => {
    setMenuOpen(false);
  }, [pathname]);

  /* Lock body scroll when drawer is open */
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  /* Portalled to <body> on purpose: rendered inside <nav> the drawer takes
     that element as its containing block, because .nav sets backdrop-filter
     and a filtered ancestor makes position:fixed resolve against IT instead
     of the viewport. inset:0 then sized the backdrop to the 71px nav bar and
     the page showed through behind the links. */
  const drawer = (
    <AnimatePresence>
      {menuOpen && (
        <motion.div
          className={styles.drawer}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
        >
          <div
            className={styles.drawerBackdrop}
            onClick={() => setMenuOpen(false)}
          />
          <motion.div
            className={styles.drawerContent}
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
          >
            {ROUTES.map((route) => {
              const isActive = pathname.startsWith(route.path);
              return (
                <a
                  key={route.path}
                  href={route.path}
                  className={styles.drawerLink}
                  data-active={isActive}
                  onClick={() => setMenuOpen(false)}
                  style={
                    isActive
                      ? ({
                          "--link-accent": route.accent,
                          "--link-active-bg": colorAlpha(route.accent, 8),
                          "--link-active-border": colorAlpha(
                            route.accent,
                            25,
                          ),
                        } as React.CSSProperties)
                      : undefined
                  }
                >
                  {route.label}
                </a>
              );
            })}

            <a
              href="/redeem"
              className={styles.drawerRedeem}
              onClick={() => setMenuOpen(false)}
            >
              Got a Code?
            </a>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );

  return (
    <nav className={styles.nav} data-scrolled={scrolled || pathname !== "/"}>
      <a href="/" className={styles.logo} aria-label="OddStack home">
        <span className={styles.logoIconWrap}>
          <img
            src="/images/applecart-cartkeeper/oddstack_logo_light.svg"
            alt=""
            className={styles.logoImg}
            aria-hidden="true"
          />
        </span>
        <span className={styles.logoText}>OddStack</span>
      </a>

      {/* Desktop links */}
      <div className={styles.links}>
        {ROUTES.map((route) => {
          const isActive = pathname.startsWith(route.path);
          return (
            <a
              key={route.path}
              href={route.path}
              className={styles.link}
              data-active={isActive}
              style={
                isActive
                  ? ({
                      "--link-accent": route.accent,
                      "--link-active-bg": colorAlpha(route.accent, 8),
                      "--link-active-border": colorAlpha(route.accent, 25),
                    } as React.CSSProperties)
                  : undefined
              }
            >
              {route.label}
            </a>
          );
        })}
      </div>

      {/* Redeem CTA — separate from nav */}
      <a
        href="/redeem"
        className={styles.redeemBtn}
        data-active={pathname.startsWith("/redeem")}
      >
        Got a Code?
      </a>

      {/* Mobile hamburger */}
      <button
        className={styles.menuToggle}
        onClick={() => setMenuOpen(!menuOpen)}
        aria-label={menuOpen ? "Close menu" : "Open menu"}
      >
        {menuOpen ? <X size={24} /> : <Menu size={24} />}
      </button>

      {mounted && createPortal(drawer, document.body)}
    </nav>
  );
}
